import api from "../lib/api";

/**
 * Validate a coupon code and get the discount amount
 * @param {string} couponCode - The coupon code to validate
 * @param {number} cartTotal - The cart total before discount
 * @param {string} customerId - Optional customer ID for user-specific validations
 * @param {Array} items - Optional cart items for product/category-specific validations
 * @returns {Promise<{valid: boolean, discount_amount: number, message: string, coupon_data: object}>}
 */
export const validateCoupon = async (couponCode, cartTotal, customerId = null, items = null, customerEmail = null) => {
  try {
    const response = await api.post("/coupons/validate", {
      coupon_code: couponCode,
      cart_total: cartTotal,
      customer_id: customerId,
      customer_email: customerEmail,
      items: items,
    });
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    return {
      valid: false,
      discount_amount: 0,
      message: typeof detail === "string" ? detail : "Failed to validate coupon",
      coupon_data: null,
    };
  }
};

/**
 * Get list of coupons for admin panel
 * @param {string} status - Filter by status: "active", "inactive", "expired", "scheduled"
 * @param {string} search - Search by coupon code or name
 * @returns {Promise<Array>}
 */
export const listAdminCoupons = async (status = null, search = null) => {
  try {
    const params = {};
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await api.get("/admin/coupons", { params });
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.detail || "Failed to fetch coupons");
  }
};

/**
 * Create a new coupon
 * @param {object} couponData - The coupon data
 * @returns {Promise<object>}
 */
export const createCoupon = async (couponData) => {
  try {
    const response = await api.post("/admin/coupons", couponData);
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.detail || "Failed to create coupon");
  }
};

/**
 * Get a specific coupon
 * @param {string} couponId - The coupon ID
 * @returns {Promise<object>}
 */
export const getCoupon = async (couponId) => {
  try {
    const response = await api.get(`/admin/coupons/${couponId}`);
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.detail || "Failed to fetch coupon");
  }
};

/**
 * Update a coupon
 * @param {string} couponId - The coupon ID
 * @param {object} couponData - The updated coupon data
 * @returns {Promise<object>}
 */
export const updateCoupon = async (couponId, couponData) => {
  try {
    const response = await api.patch(`/admin/coupons/${couponId}`, couponData);
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.detail || "Failed to update coupon");
  }
};

/**
 * Delete a coupon
 * @param {string} couponId - The coupon ID
 * @returns {Promise<object>}
 */
export const deleteCoupon = async (couponId) => {
  try {
    const response = await api.delete(`/admin/coupons/${couponId}`);
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.detail || "Failed to delete coupon");
  }
};

/**
 * Duplicate a coupon
 * @param {string} couponId - The coupon ID to duplicate
 * @returns {Promise<object>}
 */
export const duplicateCoupon = async (couponId) => {
  try {
    const response = await api.post(`/admin/coupons/${couponId}/duplicate`);
    return response.data;
  } catch (error) {
    throw new Error(error?.response?.data?.detail || "Failed to duplicate coupon");
  }
};
